/**
 * SoKno-owned analytics abstraction.
 *
 * - Fail open: never throws into page/form flows
 * - No secrets in frontend (public keys only via REACT_APP_*)
 * - Disabled when unconfigured
 * - Skips /admin by default
 * - No form-body / PII capture
 */

import { ANALYTICS_EVENTS, sanitizeEventProps } from "./events";

const PROVIDER = (process.env.REACT_APP_ANALYTICS_PROVIDER || "").trim().toLowerCase();
const ENABLED_FLAG = (process.env.REACT_APP_ANALYTICS_ENABLED || "").trim().toLowerCase();

let initialized = false;
let providerAdapter = null;

function isExplicitlyDisabled() {
  return ENABLED_FLAG === "false" || ENABLED_FLAG === "0" || ENABLED_FLAG === "off";
}

function isAdminPath(pathname = window.location?.pathname || "") {
  return pathname === "/admin" || pathname.startsWith("/admin/");
}

export function isAnalyticsConfigured() {
  if (isExplicitlyDisabled()) return false;
  if (!PROVIDER || PROVIDER === "none" || PROVIDER === "off") return false;
  if (PROVIDER === "console") return true;
  if (PROVIDER === "posthog") {
    return Boolean(process.env.REACT_APP_POSTHOG_KEY && process.env.REACT_APP_POSTHOG_HOST);
  }
  if (PROVIDER === "plausible") {
    return Boolean(process.env.REACT_APP_PLAUSIBLE_DOMAIN);
  }
  if (PROVIDER === "ga4") {
    return Boolean(process.env.REACT_APP_GA_MEASUREMENT_ID);
  }
  return false;
}

export function getAnalyticsProviderName() {
  if (!isAnalyticsConfigured()) return "disabled";
  return PROVIDER;
}

function createConsoleAdapter() {
  return {
    name: "console",
    track(event, props) {
      if (typeof console !== "undefined" && console.debug) {
        console.debug("[sokno-analytics]", event, props);
      }
    },
    page(props) {
      this.track(ANALYTICS_EVENTS.PAGE_VIEW, props);
    },
  };
}

/**
 * Provider SDKs are not bundled until a SoKno-owned vendor is selected.
 * Adapters emit via window hooks or no-op so builds stay free of third-party scripts.
 */
function createStubAdapter(name) {
  return {
    name,
    track(event, props) {
      const hook = typeof window !== "undefined" ? window.__SOKNO_ANALYTICS__ : null;
      if (hook && typeof hook.track === "function") {
        try {
          hook.track(event, props);
        } catch {
          /* fail open */
        }
      }
    },
    page(props) {
      this.track(ANALYTICS_EVENTS.PAGE_VIEW, props);
    },
  };
}

export function initAnalytics() {
  try {
    if (initialized) return getAnalyticsProviderName();
    initialized = true;
    if (!isAnalyticsConfigured()) {
      providerAdapter = null;
      return "disabled";
    }
    if (PROVIDER === "console") {
      providerAdapter = createConsoleAdapter();
    } else {
      providerAdapter = createStubAdapter(PROVIDER);
    }
    return providerAdapter.name;
  } catch {
    providerAdapter = null;
    return "disabled";
  }
}

export function track(eventName, props = {}) {
  try {
    if (!initialized) initAnalytics();
    if (!providerAdapter) return false;
    if (typeof window !== "undefined" && isAdminPath()) return false;
    const safe = sanitizeEventProps({
      route: typeof window !== "undefined" ? window.location.pathname : undefined,
      ...props,
    });
    providerAdapter.track(eventName, safe);
    return true;
  } catch {
    return false;
  }
}

export function trackPageView(extra = {}) {
  return track(ANALYTICS_EVENTS.PAGE_VIEW, {
    route: typeof window !== "undefined" ? window.location.pathname : undefined,
    ...extra,
  });
}

export function trackFormStarted(formType, extra = {}) {
  const map = {
    contact: ANALYTICS_EVENTS.CONTACT_FORM_STARTED,
    project: ANALYTICS_EVENTS.PROJECT_FORM_STARTED,
    partner: ANALYTICS_EVENTS.PARTNER_FORM_STARTED,
    casting: ANALYTICS_EVENTS.CASTING_FORM_STARTED,
    crew: ANALYTICS_EVENTS.PRODUCTION_NETWORK_FORM_STARTED,
    production_network: ANALYTICS_EVENTS.PRODUCTION_NETWORK_FORM_STARTED,
  };
  return track(map[formType] || `${formType}_form_started`, {
    form_type: formType,
    ...extra,
  });
}

export function trackFormCompleted(formType, extra = {}) {
  const map = {
    contact: ANALYTICS_EVENTS.CONTACT_FORM_COMPLETED,
    project: ANALYTICS_EVENTS.PROJECT_FORM_COMPLETED,
    partner: ANALYTICS_EVENTS.PARTNER_FORM_COMPLETED,
    casting: ANALYTICS_EVENTS.CASTING_FORM_COMPLETED,
    crew: ANALYTICS_EVENTS.PRODUCTION_NETWORK_FORM_COMPLETED,
    production_network: ANALYTICS_EVENTS.PRODUCTION_NETWORK_FORM_COMPLETED,
  };
  return track(map[formType] || `${formType}_form_completed`, {
    form_type: formType,
    ...extra,
  });
}

export { ANALYTICS_EVENTS, sanitizeEventProps };
